// hooks/useCampaigns.js - React hooks for campaign management using API
import { useMemo, useCallback, useState, useEffect } from 'react';
import { Campaign } from '../models/Campaign.js';
import CampaignAPI from '../services/api/campaignAPI';

// Main hook for campaign management
export function useCampaigns() {
  const [rawCampaigns, setRawCampaigns] = useState([]);
  const [campaignExecutions, setCampaignExecutions] = useState({});
  const [loading, setLoading] = useState(false);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const campaigns = await CampaignAPI.getCampaigns();
      setRawCampaigns(campaigns);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Convert API data to Campaign instances
  const campaignInstances = useMemo(() => {
    return rawCampaigns.map(campaign => Campaign.fromObject({
      ...campaign,
      created_at: campaign.created_at || new Date().toISOString(),
      updated_at: campaign.updated_at || new Date().toISOString()
    }));
  }, [rawCampaigns]);

  // Enhanced campaigns with execution data
  const campaignsWithExecutions = useMemo(() => {
    return campaignInstances.map(campaign => ({
      ...campaign,
      latestExecution: campaignExecutions[campaign.id] || null,
      executionStatus: getExecutionStatus(campaignExecutions[campaign.id])
    }));
  }, [campaignInstances, campaignExecutions]);

  // Helper function to determine execution status
  function getExecutionStatus(execution) {
    if (!execution) {
      return {
        status: 'never-ran',
        label: 'Never ran',
        color: 'gray'
      };
    }

    if (execution.status === 'processing' || execution.status === 'queued') {
      return {
        status: 'running',
        label: 'Running now',
        color: 'blue'
      };
    }

    if (execution.status === 'completed') {
      const hasFailures = execution.messagesFailed > 0;
      return {
        status: 'completed',
        label: hasFailures ? 'Completed with issues' : 'Completed successfully',
        color: hasFailures ? 'orange' : 'green',
        lastRun: execution.executionDate,
        messagesSent: execution.messagesSent,
        messagesFailed: execution.messagesFailed
      };
    }

    if (execution.status === 'failed') {
      return {
        status: 'failed',
        label: 'Failed',
        color: 'red',
        lastRun: execution.executionDate,
        error: execution.errorMessage
      };
    }

    return {
      status: 'unknown',
      label: 'Unknown',
      color: 'gray'
    };
  }

  // Load campaign executions
  const loadCampaignExecutions = useCallback(async () => {
    if (campaignInstances.length === 0) return;

    setExecutionsLoading(true);
    try {
      const campaignIds = campaignInstances.map(c => c.id);
      const executions = await CampaignAPI.getMultipleCampaignExecutions(campaignIds);
      setCampaignExecutions(executions);
    } catch (error) {
      console.error('Failed to load campaign executions:', error);
    } finally {
      setExecutionsLoading(false);
    }
  }, [campaignInstances]);

  // Load executions when campaigns change
  useEffect(() => {
    if (campaignInstances.length > 0) {
      loadCampaignExecutions();
    }
  }, [loadCampaignExecutions]);

  // Create campaign
  const createCampaign = useCallback(async (campaignData) => {
    try {
      setError(null);

      // Validate the campaign data
      const validation = Campaign.validate(campaignData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate names
      const existingCampaign = campaignInstances.find(
        campaign => campaign.name.toLowerCase() === campaignData.name.toLowerCase()
      );
      if (existingCampaign) {
        throw new Error('A campaign with this name already exists');
      }

      const finalCampaignData = { ...campaignData };

      // Create new campaign via API
      const newCampaign = await CampaignAPI.createCampaign(finalCampaignData);

      // Update local state
      setRawCampaigns(prev => [...prev, newCampaign]);

      return { success: true, campaign: Campaign.fromObject(newCampaign) };
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [campaignInstances]);

  // Update campaign
  const updateCampaign = useCallback(async (id, updateData) => {
    try {
      setError(null);

      // Find the campaign
      const existingCampaign = rawCampaigns.find(c => c.id === id);
      if (!existingCampaign) {
        throw new Error('Campaign not found');
      }

      // Create updated campaign data
      const updatedData = { ...existingCampaign, ...updateData };

      // Validate the updated data
      const validation = Campaign.validate(updatedData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate names (excluding current campaign)
      if (updateData.name && updateData.name !== existingCampaign.name) {
        const duplicateCampaign = campaignInstances.find(campaign =>
          campaign.name.toLowerCase() === updateData.name.toLowerCase() && campaign.id !== id
        );
        if (duplicateCampaign) {
          throw new Error('A campaign with this name already exists');
        }
      }

      // Update the campaign via API
      const updatedCampaign = await CampaignAPI.updateCampaign(id, updateData);

      // Update local state
      setRawCampaigns(prev => prev.map(campaign =>
        campaign.id === id ? updatedCampaign : campaign
      ));

      return { success: true, campaign: Campaign.fromObject(updatedCampaign) };
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [rawCampaigns, campaignInstances]);

  // Toggle campaign status
  const toggleCampaign = useCallback(async (id) => {
    try {
      setError(null);

      const existingCampaign = rawCampaigns.find(c => {
        return c.id === id
      });
      if (!existingCampaign) {
        throw new Error('Campaign not found');
      }

      const newStatus = existingCampaign.status === 'Active' ? 'Inactive' : 'Active';
      const updatedCampaign = await CampaignAPI.updateCampaign(id, {
        status: newStatus
      });

      // Update local state
      setRawCampaigns(prev => prev.map(campaign =>
        campaign.id === id ? updatedCampaign : campaign
      ));

      return { success: true, campaign: Campaign.fromObject(updatedCampaign) };
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [rawCampaigns]);

  // Delete campaign
  const deleteCampaign = useCallback(async (id) => {
    try {
      setError(null);

      await CampaignAPI.deleteCampaign(id);

      // Update local state
      setRawCampaigns(prev => prev.filter(campaign => campaign.id !== id));

      return { success: true };
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Launch campaign manually (for one-time campaigns)
  const launchCampaign = useCallback(async (campaignData) => {
    const id = campaignData.id
    try {
      setError(null);

      // Find the campaign
      const existingCampaign = rawCampaigns.find(c => {
        return c.id === id
      }); 
      if (!existingCampaign) {
        throw new Error('Campaign not found');
      }

      // Validate that it's a one-time campaign
      if (existingCampaign.type !== 'One-Time') {
        throw new Error('Only one-time campaigns can be launched manually');
      }

      // Launch the campaign via API
      const launchResult = await CampaignAPI.launchCampaign(id);

      // Update local state with the launch results
      setRawCampaigns(prev => prev.map(campaign =>
        campaign.id === id ? {
          ...campaign,
          lastDispatchDate: new Date().toISOString().split('T')[0],
          lastDispatchStatus: 'completed',
          lastDispatchMessageCount: launchResult.messagesSent || 0,
          totalDispatches: (campaign.totalDispatches || 0) + 1,
          totalMessagesSent: (campaign.totalMessagesSent || 0) + (launchResult.messagesSent || 0),
          updated_at: new Date().toISOString()
        } : campaign
      ));

      return {
        success: true,
        result: {
          messagesSent: launchResult.messagesSent || 0,
          customersFound: launchResult.customersFound || 0
        }
      };
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [rawCampaigns]);

  return {
    campaigns: campaignsWithExecutions,
    executions: campaignExecutions,
    loading,
    error,
    createCampaign,
    updateCampaign,
    toggleCampaign,
    deleteCampaign,
    launchCampaign,
    clearError,
    refreshCampaigns: loadCampaigns
  };
}

// Hook for campaign statistics
export function useCampaignStats() {
  const { campaigns, loading } = useCampaigns();

  const stats = useMemo(() => {
    const total = campaigns.length;
    const active = campaigns.filter(c => c.status === 'Active').length;
    const inactive = total - active;

    const byCampaignType = campaigns.reduce((acc, campaign) => {
      acc[campaign.type] = (acc[campaign.type] || 0) + 1;
      return acc;
    }, {});

    const scheduledToday = campaigns.filter(c => c.isScheduledToday()).length;
    const overdue = campaigns.filter(c => c.isOverdue()).length;

    return {
      total,
      active,
      inactive,
      byCampaignType,
      scheduledToday,
      overdue
    };
  }, [campaigns]);

  return { stats, loading };
}

// Hook for campaign search
export function useCampaignSearch(campaigns, initialQuery = '') {
  const [query, setQuery] = useState(initialQuery);

  const results = useMemo(() => {
    if (!query.trim()) {
      return campaigns;
    }

    const searchTerm = query.toLowerCase();
    return campaigns.filter(campaign =>
      campaign.name.toLowerCase().includes(searchTerm) ||
      campaign.targetAudience.toLowerCase().includes(searchTerm) ||
      campaign.messageContent.toLowerCase().includes(searchTerm)
    );
  }, [campaigns, query]);

  return {
    query,
    setQuery,
    results
  };
}

// Hook for single campaign
export function useCampaign(id) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCampaign = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const campaignData = await CampaignAPI.getCampaignById(id);
      if (campaignData) {
        setCampaign(Campaign.fromObject(campaignData));
      } else {
        setCampaign(null);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to load campaign:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  return { campaign, loading, error, refreshCampaign: loadCampaign };
}

// Hook for filtered campaigns
export function useFilteredCampaigns(filters = {}) {
  const { campaigns, loading, error } = useCampaigns();

  const filteredCampaigns = useMemo(() => {
    let filtered = [...campaigns];

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(campaign => campaign.status === filters.status);
    }

    // Filter by type
    if (filters.type) {
      filtered = filtered.filter(campaign => campaign.type === filters.type);
    }

    // Filter by search query
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm) ||
        campaign.targetAudience.toLowerCase().includes(searchTerm) ||
        campaign.messageContent.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [campaigns, filters]);

  return {
    campaigns: filteredCampaigns,
    loading,
    error
  };
}